import type { ChunkHash } from "./protocol";

export type { ChunkHash };

/**
 * 코퍼스 볼륨(SQLite .aknpkg) 다운로드의 순수 로직.
 *
 * 브라우저/OPFS에 의존하지 않는다. `node --test`에서 그대로 가져다 검증한다.
 * 모델(ONNX)은 64MB 청크 + Cache API를 쓰지만, 볼륨은 8~16MB 청크 + OPFS를 쓴다.
 */
export const VOLUME_CHUNK_SIZE = 8 * 1024 * 1024;
export const MIN_VOLUME_CHUNK_SIZE = 8 * 1024 * 1024;
export const MAX_VOLUME_CHUNK_SIZE = 16 * 1024 * 1024;
/** 모델 청크. 볼륨에 재사용하면 1.5GB에서 메모리가 터진다. */
export const MODEL_CHUNK_SIZE = 64 * 1024 * 1024;
/**
 * OPFS 안의 볼륨 디렉터리. 실제 정의는 `packages/core/src/volumes.ts`에 있다.
 * (검색 워커·SetupPanel·volume-store와 같은 값을 쓰기 위해 재수출만 한다.)
 */
export { VOLUME_DIRECTORY } from "../../core/src/volumes.ts";

const HEX64 = /^[0-9a-f]{64}$/;

export type VolumeErrorCode =
  | "manifest"
  | "unavailable"
  | "range"
  | "size"
  | "hash"
  | "network"
  | "storage"
  | "cancelled"
  | "aborted";

export class VolumeError extends Error {
  readonly code: VolumeErrorCode;

  constructor(code: VolumeErrorCode, message: string) {
    super(message);
    this.name = "VolumeError";
    this.code = code;
  }
}

export interface VolumeArtifact {
  url: string;
  size: number;
  sha256: string;
  chunkSize: number;
  chunks: ChunkHash[];
  version?: string;
  format?: string;
}

export interface VolumeCheckpoint {
  sha256: string;
  size: number;
  chunkSize: number;
  bytesDone: number;
  completedChunks: number;
  /** 지금까지 검증한 청크 해시(순서대로). 매니페스트 prefix와 대조한다. */
  chunkHashes: string[];
  updatedAt: number;
}

export interface VolumeCheckpointStore {
  get(key: string): Promise<VolumeCheckpoint | null>;
  set(key: string, checkpoint: VolumeCheckpoint): Promise<void>;
  clear(key: string): Promise<void>;
}

/** 워커/폴백이 공유하는 OPFS 접근 인터페이스. */
export interface VolumeSink {
  size(file: string): Promise<number | null>;
  read(file: string, offset: number, length: number): Promise<Uint8Array>;
  write(file: string, offset: number, bytes: Uint8Array): Promise<void>;
  truncate(file: string, size: number): Promise<void>;
  remove(file: string): Promise<void>;
  flush(file: string): Promise<void>;
  promote(from: string, to: string): Promise<void>;
  close(): Promise<void>;
}

export type VolumeWriterRequest =
  | { id: number; op: "ping" }
  | { id: number; op: "size"; file: string }
  | { id: number; op: "read"; file: string; offset: number; length: number }
  | { id: number; op: "write"; file: string; offset: number; bytes: ArrayBuffer }
  | { id: number; op: "truncate"; file: string; size: number }
  | { id: number; op: "remove"; file: string }
  | { id: number; op: "flush"; file: string }
  | { id: number; op: "promote"; from: string; to: string }
  | { id: number; op: "close" };

export type VolumeWriterResponse =
  | { id: number; ok: true; result: unknown }
  | { id: number; ok: false; error: string };

export function isSha256Hex(value: unknown): value is string {
  return typeof value === "string" && HEX64.test(value);
}

export function resolveVolumeChunkSize(requested?: number): number {
  const value = requested ?? VOLUME_CHUNK_SIZE;
  if (
    !Number.isInteger(value) ||
    value < MIN_VOLUME_CHUNK_SIZE ||
    value > MAX_VOLUME_CHUNK_SIZE
  ) {
    throw new VolumeError(
      "manifest",
      `volume chunkSize must be ${MIN_VOLUME_CHUNK_SIZE}-${MAX_VOLUME_CHUNK_SIZE} bytes (got ${value})`,
    );
  }
  return value;
}

/** 전체 파일 크기와 청크 크기로 청크 레이아웃을 만든다(에어갭 가져오기용). */
export function makeChunkLayout(size: number, chunkSize: number): ChunkHash[] {
  if (!Number.isInteger(size) || size <= 0) throw new VolumeError("size", "invalid volume size");
  resolveVolumeChunkSize(chunkSize);
  const chunks: ChunkHash[] = [];
  for (let offset = 0, index = 0; offset < size; offset += chunkSize, index++) {
    chunks.push({
      index,
      size: Math.min(chunkSize, size - offset),
      // 레이아웃 전용. 실제 해시는 스트리밍하면서 채운다.
      sha256: "",
    });
  }
  return chunks;
}

export function findManifestArtifact(document: unknown, key: string): unknown {
  const artifacts = (document as { artifacts?: Record<string, unknown> } | null)?.artifacts;
  return artifacts?.[key];
}

export function parseVolumePlaceholder(
  document: unknown,
  key: string,
): { pending: true; note?: string } | null {
  const entry = findManifestArtifact(document, key);
  if (!entry || typeof entry !== "object") return null;
  const record = entry as Record<string, unknown>;
  if (record.pending !== true) return null;
  return {
    pending: true,
    ...(typeof record.note === "string" ? { note: record.note } : {}),
  };
}

/**
 * manifest의 코퍼스 artifact를 검증한다.
 * - pending/누락이면 `unavailable`
 * - URL/해시/청크 합이 맞지 않으면 `manifest`
 */
export function parseVolumeArtifact(document: unknown, key: string): VolumeArtifact {
  const entry = findManifestArtifact(document, key);
  if (!entry || typeof entry !== "object") {
    throw new VolumeError("unavailable", `volume artifact missing: ${key}`);
  }
  const record = entry as Record<string, unknown>;
  if (record.pending === true) {
    throw new VolumeError("unavailable", `volume artifact pending: ${key}`);
  }

  const url = record.url;
  if (typeof url !== "string" || url.length === 0) {
    throw new VolumeError("manifest", `volume url invalid: ${key}`);
  }
  const size = record.size;
  if (typeof size !== "number" || !Number.isInteger(size) || size <= 0) {
    throw new VolumeError("manifest", `volume size invalid: ${key}`);
  }
  const sha256 = record.sha256;
  if (!isSha256Hex(sha256)) {
    throw new VolumeError("manifest", `volume sha256 invalid: ${key}`);
  }
  const chunkSize = record.chunkSize;
  if (typeof chunkSize !== "number") {
    throw new VolumeError("manifest", `volume chunkSize invalid: ${key}`);
  }
  resolveVolumeChunkSize(chunkSize);

  if (!Array.isArray(record.chunks) || record.chunks.length === 0) {
    throw new VolumeError("manifest", `volume chunks invalid: ${key}`);
  }

  const chunks: ChunkHash[] = [];
  let total = 0;
  for (let index = 0; index < record.chunks.length; index++) {
    const raw = record.chunks[index];
    if (!raw || typeof raw !== "object") {
      throw new VolumeError("manifest", `volume chunk invalid: ${key}#${index}`);
    }
    const chunk = raw as Record<string, unknown>;
    if (chunk.index !== index) {
      throw new VolumeError("manifest", `volume chunk index invalid: ${key}#${index}`);
    }
    if (typeof chunk.size !== "number" || !Number.isInteger(chunk.size) || chunk.size <= 0) {
      throw new VolumeError("manifest", `volume chunk size invalid: ${key}#${index}`);
    }
    if (chunk.size > chunkSize) {
      throw new VolumeError("manifest", `volume chunk too large: ${key}#${index}`);
    }
    if (index < record.chunks.length - 1 && chunk.size !== chunkSize) {
      throw new VolumeError("manifest", `volume chunk not full: ${key}#${index}`);
    }
    if (!isSha256Hex(chunk.sha256)) {
      throw new VolumeError("manifest", `volume chunk sha256 invalid: ${key}#${index}`);
    }
    chunks.push({ index, size: chunk.size, sha256: chunk.sha256 });
    total += chunk.size;
  }
  if (total !== size) {
    throw new VolumeError("manifest", `volume chunks do not sum to size: ${key}`);
  }

  return {
    url,
    size,
    sha256,
    chunkSize,
    chunks,
    ...(typeof record.version === "string" ? { version: record.version } : {}),
    ...(typeof record.format === "string" ? { format: record.format } : {}),
  };
}

export interface ByteRange {
  start: number;
  /** inclusive */
  end: number;
  total: number;
}

/** `Content-Range: bytes 0-99/1234` 파싱. 형식이 다르면 null. */
export function parseContentRange(value: string | null | undefined): ByteRange | null {
  if (typeof value !== "string") return null;
  const match = /^bytes (\d+)-(\d+)\/(\d+)$/.exec(value.trim());
  if (!match) return null;
  const start = Number(match[1]);
  const end = Number(match[2]);
  const total = Number(match[3]);
  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(end) || !Number.isSafeInteger(total)) {
    return null;
  }
  if (start < 0 || end < start || total <= end) return null;
  return { start, end, total };
}

export interface ChunkRange extends ByteRange {
  index: number;
  length: number;
}

export function chunkByteRange(chunks: ChunkHash[], index: number): ChunkRange {
  const chunk = chunks[index];
  if (!chunk) throw new VolumeError("range", `chunk index out of bounds: ${index}`);
  let start = 0;
  for (let i = 0; i < index; i++) start += chunks[i]!.size;
  return {
    index,
    start,
    end: start + chunk.size - 1,
    length: chunk.size,
    total: chunks.reduce((sum, item) => sum + item.size, 0),
  };
}

export interface ChunkResponseInput {
  status: number;
  contentRange: string | null;
  contentLength: string | null;
  expectedStart: number;
  expectedEnd: number;
  expectedTotal: number;
}

/** Range 응답이 우리가 요청한 구간과 정확히 일치하는지 검사한다. */
export function checkChunkResponse(input: ChunkResponseInput): { ok: true } | { ok: false; reason: string } {
  if (input.status !== 206) return { ok: false, reason: `status ${input.status}` };
  const range = parseContentRange(input.contentRange);
  if (!range) return { ok: false, reason: "content-range missing" };
  if (range.start !== input.expectedStart || range.end !== input.expectedEnd) {
    return { ok: false, reason: `content-range ${range.start}-${range.end}` };
  }
  if (range.total !== input.expectedTotal) {
    return { ok: false, reason: `total ${range.total} != ${input.expectedTotal}` };
  }
  const length = input.expectedEnd - input.expectedStart + 1;
  if (input.contentLength !== null) {
    const raw = Number(input.contentLength);
    if (!Number.isFinite(raw) || raw !== length) {
      return { ok: false, reason: `content-length ${input.contentLength} != ${length}` };
    }
  }
  return { ok: true };
}

export interface VolumeResumePlan {
  completedChunks: number;
  bytesDone: number;
  /** `.part`를 이 크기로 잘라낸 뒤 이어받는다. */
  truncateTo: number;
  restart: boolean;
  reason?: string;
}

/**
 * 체크포인트 + `.part` 실제 크기로 이어받기 지점을 정한다.
 *
 * 체크포인트의 바이트 수는 믿지 않고 매니페스트 청크 크기로 다시 계산한다.
 * `.part`가 체크포인트보다 크면(체크포인트 기록 전 중단) 검증된 지점까지 자른다.
 */
export function planVolumeResume(input: {
  artifact: Pick<VolumeArtifact, "size" | "sha256" | "chunkSize" | "chunks">;
  checkpoint: VolumeCheckpoint | null;
  partSize: number | null;
}): VolumeResumePlan {
  const { artifact, checkpoint, partSize } = input;
  const total = artifact.chunks.reduce((sum, chunk) => sum + chunk.size, 0);
  if (total !== artifact.size) {
    throw new VolumeError("manifest", "artifact chunks do not sum to size");
  }

  const restart = (reason: string): VolumeResumePlan => ({
    completedChunks: 0,
    bytesDone: 0,
    truncateTo: 0,
    restart: true,
    reason,
  });

  if (!checkpoint) return restart("no checkpoint");
  if (
    checkpoint.sha256 !== artifact.sha256 ||
    checkpoint.size !== artifact.size ||
    checkpoint.chunkSize !== artifact.chunkSize
  ) {
    return restart("artifact changed");
  }

  let completed = checkpoint.completedChunks;
  if (!Number.isInteger(completed) || completed < 0 || completed > artifact.chunks.length) {
    return restart("checkpoint out of range");
  }
  if (
    !Array.isArray(checkpoint.chunkHashes) ||
    checkpoint.chunkHashes.length < completed
  ) {
    return restart("checkpoint hashes missing");
  }
  for (let index = 0; index < completed; index++) {
    if (checkpoint.chunkHashes[index] !== artifact.chunks[index]!.sha256) {
      return restart(`checkpoint hash mismatch at ${index}`);
    }
  }

  let bytesDone = 0;
  for (let index = 0; index < completed; index++) bytesDone += artifact.chunks[index]!.size;

  if (partSize === null || partSize < bytesDone) {
    return restart("part file missing or short");
  }
  return {
    completedChunks: completed,
    bytesDone,
    truncateTo: bytesDone,
    restart: false,
  };
}

export type FinalizeAction = "keep-existing" | "promote" | "restart";

/**
 * 검증이 끝난 뒤 `.part`를 최종 이름으로 올릴지 결정한다.
 * 기존 최종 파일이 기대 크기면 다시 받지 않는다.
 */
export function decideFinalize(input: {
  verified: boolean;
  partSize: number;
  expectedSize: number;
  existingSize: number | null;
}): FinalizeAction {
  if (input.existingSize === input.expectedSize) return "keep-existing";
  if (input.verified && input.partSize === input.expectedSize) return "promote";
  return "restart";
}

/**
 * 청크 해시 누적기.
 *
 * 검증된 청크만 받아들이고, 매니페스트의 순서/크기/해시와 다르면 즉시 실패한다.
 * WebCrypto에는 증분 digest가 없으므로 "청크별 SHA-256을 매니페스트와 대조"가
 * 전체 파일 검증을 대신한다. (최종 verifying 단계에서 디스크를 다시 읽어 재확인)
 */
export class ChunkAccumulator {
  private readonly chunks: ChunkHash[];
  private completed: number;
  private bytes: number;
  private readonly hashes: string[];

  constructor(chunks: ChunkHash[], startIndex = 0) {
    if (!Number.isInteger(startIndex) || startIndex < 0 || startIndex > chunks.length) {
      throw new VolumeError("size", `invalid accumulator start: ${startIndex}`);
    }
    this.chunks = chunks;
    this.completed = startIndex;
    this.bytes = 0;
    for (let index = 0; index < startIndex; index++) this.bytes += chunks[index]!.size;
    this.hashes = chunks.slice(0, startIndex).map((chunk) => chunk.sha256);
  }

  get state(): { completedChunks: number; bytesDone: number; chunkHashes: string[] } {
    return {
      completedChunks: this.completed,
      bytesDone: this.bytes,
      chunkHashes: [...this.hashes],
    };
  }

  record(index: number, size: number, sha256: string): void {
    if (index !== this.completed) {
      throw new VolumeError("size", `chunk out of order: expected ${this.completed}, got ${index}`);
    }
    const expected = this.chunks[index];
    if (!expected) throw new VolumeError("size", `chunk index out of bounds: ${index}`);
    if (size !== expected.size) {
      throw new VolumeError("size", `chunk ${index} size mismatch: expected ${expected.size}, got ${size}`);
    }
    if (sha256 !== expected.sha256) {
      throw new VolumeError("hash", `chunk ${index} hash mismatch`);
    }
    this.hashes.push(sha256);
    this.bytes += size;
    this.completed = index + 1;
  }
}
